import { useEffect, useState } from "react";
import ActionToolType from "./ActionToolType";
import ActionToolEnum from "./ActionToolEnum";
import SntListIcon from "@icons/SntListIcon";
import SntMapIcon from "@icons/SntMapIcon";
import Toolbar from "./Toolbar";
import AwesomeIcon from "@icons/AwesomeIcon";
import { useOrg } from "@/contexts/OrgContext";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { hasFeature } from "@/hooks/useFeatures";
import { hasPermission } from "@/hooks/usePermissions";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { UserPermission } from "@/types/generated/UserPermission";

// Toolbar.js expects specific structure based on default props
interface ToolbarItem {
  id: string;
  title: string;
  icon: string | React.ReactNode;
  link?: string;
  fullLinkInNewTab?: string;
  customActiveClass?: string;
  isHideText?: boolean;
  isAlwayShow?: boolean;
  isSelectable?: boolean;
  disabled?: boolean;
  disabledTitle?: string;
}

interface ActionToolOption {
  id: string;
  type?: string;
  title?: string;
  icon?: React.ReactNode;
  link?: string;
  fullLinkInNewTab?: string;
  customActiveClass?: string;
  isHideText?: boolean;
  isAlwayShow?: boolean;
  isSelectable?: boolean;
  disabled?: boolean;
  disabledTitle?: string;
}

interface HeroAction {
  isShow: boolean;
  label: string;
  isManage?: boolean;
  link?: string;
  fullLinkInNewTab?: string;
  customActiveClass?: string;
  onClick?: () => void;
}

interface GroupConfig {
  viewMode?: string;
  onChange?: (mode: string) => void;
}

interface ActionToolProps {
  list?: ActionToolOption[];
  group?: GroupConfig;
  heroAction?: HeroAction;
  onClick?: (id: string) => void;
}

interface OrgContextType {
  scopeOrgId: number | null;
  updateOrg: (newOrgId: number | null) => void;
}

const ActionTool = ({
  list,
  group,
  heroAction,
  onClick,
}: ActionToolProps) => {
  const orgContext = useOrg() as OrgContextType | null;
  const scopeOrgId = orgContext?.scopeOrgId ?? null;

  const [sections, setSections] = useState<ToolbarItem[][]>([]);
  const [fixedSections, setFixedSections] = useState<ToolbarItem[][]>([]);

  useEffect(() => {
    const listData = list || [];
    let datas = listData.filter(
      (option) => option.type === ActionToolType.data
    );

    // check condition for exporting data action
    if (
      !hasFeature(FeatureKey.report_generator) ||
      !hasPermission(UserPermission.MENU_HOME_REPORT_LIST) ||
      !hasPermission(UserPermission.REPORTING_MANAGE)
    ) {
      datas = datas.filter(
        (action) => action.id !== ActionToolEnum.export_report
      );
    }
    // check condition for import_tracker_settings action
    if (!hasPermission(UserPermission.DEVICE_UPDATE)) {
      datas = datas.filter(
        (action) => action.id !== ActionToolEnum.import_tracker_settings
      );
    }
    // check condition for bulk operation action
    if (
      !hasFeature(FeatureKey.bulk_operations) ||
      !(
        hasPermission(UserPermission.DEVICE_ACTIVATE) ||
        hasPermission(UserPermission.DEVICE_UPDATE) ||
        hasPermission(UserPermission.SUBSCRIPTION_MANAGE) ||
        hasPermission(UserPermission.DEVICE_UTILIZATION_DATA_MANAGE) ||
        hasPermission(UserPermission.DEVICE_ASSIGN) ||
        hasPermission(UserPermission.DEVICE_REMOTE_SETTING_MANAGE) ||
        hasPermission(UserPermission.PROCESS_MANAGE) ||
        hasPermission(UserPermission.QUEUE_FIRMWARE_UPGRADE)
      )
    ) {
      datas = datas.filter(
        (action) => action.id !== ActionToolEnum.bulk_operation
      );
    }

    const others = listData.filter(
      (action) => action.type !== ActionToolType.data
    );

    const data: ToolbarItem[][] = [];
    // convert heroAction option to section option
    if (heroAction && heroAction.isShow) {
      data.push([
        {
          id: "heroAction",
          title: heroAction.label,
          icon: heroAction.isManage ? (
            <AwesomeIcon icon="gear"></AwesomeIcon>
          ) : (
            <AwesomeIcon icon="plus"></AwesomeIcon>
          ),
          link: getLink(heroAction, scopeOrgId),
          fullLinkInNewTab: heroAction.fullLinkInNewTab,
          customActiveClass: heroAction.customActiveClass,
        },
      ]);
    }

    if (datas.length > 0) {
      data.push(datas as ToolbarItem[]);
    }
    if (others.length > 0) {
      data.push(others as ToolbarItem[]);
    }

    if (group) {
      setFixedSections([
        [
          {
            id: "LIST",
            title: translate(LanguageKey.list_key),
            isHideText: true,
            isAlwayShow: true,
            icon: <SntListIcon />,
          },
          {
            id: "MAP",
            title: translate(LanguageKey.map_key),
            isHideText: true,
            isAlwayShow: true,
            icon: <SntMapIcon />,
          },
        ],
      ]);
    }

    setSections(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroAction, list]);

  const getLink = (heroAction: HeroAction, selectedOrgId: number | null): string | undefined => {
    if (heroAction && heroAction.link) {
      if (heroAction.link.includes("&org=")) {
        return heroAction.link;
      } else if (
        selectedOrgId !== 0 &&
        selectedOrgId !== null &&
        heroAction.link.includes("?")
      ) {
        if (heroAction.link.includes("?")) {
          return `${heroAction.link}&org=${selectedOrgId}`;
        } else {
          return `${heroAction.link}?org=${selectedOrgId}`;
        }
      }

      return heroAction.link;
    }
  };

  const handleClick = (id: string): void => {
    if (id === "heroAction") {
      if (heroAction?.onClick) {
        heroAction.onClick();
      }
    } else if (id === "MAP" || id === "LIST") {
      if (group?.onChange) {
        group.onChange(id);
      }
    } else {
      if (onClick) {
        onClick(id);
      }
    }
  };

  return (
    <>
      <Toolbar
        sections={sections as unknown as { id: string; title: string; icon: string; link: string }[][]}
        fixedSections={fixedSections as unknown as { id: string; title: string; icon: string; link: string }[][]}
        handleClick={handleClick as () => void}
        selectedId={group?.viewMode}
      ></Toolbar>
    </>
  );
};

export default ActionTool;
